import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { IOvertimeParam } from '../../../../models/overtime/OvertimeModel';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import Popup from '../../../../components/common-components/pop-up/Popup';
import styles from './NewOvertimePopup.module.css';
import { toast } from 'react-toastify';

const NewOvertimePopup = () => {
  const { popupStore, overtimeStore, authStore } = useStore();
  const [formData, setFormData] = useState<IOvertimeParam>({
    startDate: new Date(),
    endDate: new Date(),
    description: '',
    status: 'Pending',
    overtimeType: 'Overtime',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Form açıldığında tarihleri bugünün tarihi ile başlat
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setFormData(prev => ({
      ...prev,
      startDate: today,
      endDate: tomorrow,
    }));
  }, []);

  const calculateTotalHours = (start: Date, end: Date): number => {
    const diffInMs = end.getTime() - start.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.round(diffInHours * 100) / 100; // 2 decimal places
  };

  const formatDateTimeForInput = (date: Date): string => {
    // Geçersiz tarih kontrolü
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    // Basit format - timezone sorununu önlemek için
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeFromInput = (inputValue: string): Date => {
    if (!inputValue) {
      return new Date();
    }

    // Basit parse - timezone sorununu önlemek için
    const date = new Date(inputValue);

    // Geçersiz tarih kontrolü
    if (isNaN(date.getTime())) {
      return new Date();
    }

    return date;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = 'Başlangıç tarihi gereklidir';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi gereklidir';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
    }

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Açıklama gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    popupStore.setIsNewOvertimeOpen(false);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setFormData({
      startDate: today,
      endDate: tomorrow,
      description: '',
      status: 'Pending',
      overtimeType: 'Overtime',
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof IOvertimeParam, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Hata varsa temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleFormSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    (async () => {
      try {
        const totalHours = calculateTotalHours(formData.startDate, formData.endDate);

        const overtimeData: IOvertimeParam = {
          ...formData,
          userId: authStore.user?.id?.toString(),
          totalHours: totalHours,
        };

        const response = await overtimeStore.createOvertime(overtimeData);
        if (response.error) {
          setIsSubmitting(false);
          return;
        }
        popupStore.setIsNewOvertimeOpen(false);

        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        setFormData({
          startDate: today,
          endDate: tomorrow,
          description: '',
          status: 'Pending',
          overtimeType: 'Overtime',
        });
        setErrors({});
      } catch (error) {
        console.error('Error creating overtime:', error);
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  return (
    <Popup
      isOpen={popupStore.isNewOvertimeOpen}
      onClose={handleClose}
      title="Yeni Mesai Kaydı"
      footer={
        <>
          <CommonButton type="cancel" onClick={handleClose} disabled={isSubmitting}>
            İptal
          </CommonButton>
          <CommonButton type="save" onClick={handleFormSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
          </CommonButton>
        </>
      }
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleFormSubmit();
        }}
      >
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Başlangıç Tarihi</label>
            <input
              type="datetime-local"
              className={styles.formInput}
              value={formData.startDate ? formatDateTimeForInput(formData.startDate) : ''}
              onChange={e => handleInputChange('startDate', parseDateTimeFromInput(e.target.value))}
            />
            {errors.startDate && <div className={styles.errorMessage}>{errors.startDate}</div>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Bitiş Tarihi</label>
            <input
              type="datetime-local"
              className={styles.formInput}
              value={formData.endDate ? formatDateTimeForInput(formData.endDate) : ''}
              onChange={e => handleInputChange('endDate', parseDateTimeFromInput(e.target.value))}
            />
            {errors.endDate && <div className={styles.errorMessage}>{errors.endDate}</div>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Toplam Saat</label>
          <input
            type="text"
            className={styles.formInput}
            value={
              formData.startDate && formData.endDate
                ? calculateTotalHours(formData.startDate, formData.endDate) + ' saat'
                : '0 saat'
            }
            readOnly
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Açıklama</label>
          <textarea
            className={styles.formTextarea}
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Mesai açıklamasını giriniz..."
          />
          {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
        </div>
      </form>
    </Popup>
  );
};

export default observer(NewOvertimePopup);
